import { Col, Form, Input, InputNumber, List, Row, Select } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import {
  MarkupRange,
  PartialMarkup,
  ReplaceTypeEnum,
} from 'domains/markupDomain';
import { clone, cloneDeep, uniqueId } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RULE_PRECISION, RULE_TEXT_FIXED_LEN } from 'utils/validators';

const RangeForm = (props: {
  markupRange: MarkupRange;
  onSave: (range: MarkupRange) => void;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isRevshop = useAppSelector(
    (state) => state.markup.contextMode === 'revshop'
  );
  useEffect(() => {
    form.setFieldsValue(props.markupRange);
  }, [form, props.markupRange]);
  const onSave = (values: Partial<MarkupRange>) => {
    const updated: MarkupRange = { ...props.markupRange };
    updated.lowCost = Number(values.lowCost);
    if (updated.hasHighCost) updated.highCost = Number(values.highCost);
    if (updated.highCost <= updated.lowCost) {
      form.setFields([{ name: 'highCost', errors: [t('common.highLow')] }]);
      return;
    }
    updated.markupPct = Number(values.markupPct);
    updated.skuReplace = values.skuReplace || ReplaceTypeEnum.NO_ADDITIONAL;
    props.onSave(updated);
  };
  return (
    <Form
      size="small"
      layout="inline"
      form={form}
      initialValues={props.markupRange}
      onFinish={onSave}
      style={{ paddingTop: 8 }}
    >
      <Form.Item name="lowCost" rules={[RULE_PRECISION(2)]} label="Low">
        <Input prefix="$" id={uniqueId('hightCost')} style={{ width: 80 }} />
      </Form.Item>
      {props.markupRange.hasHighCost ? (
        <Form.Item name="highCost" rules={[RULE_PRECISION(2)]} label="High">
          <Input prefix="$" id={uniqueId('hightCost')} style={{ width: 80 }} />
        </Form.Item>
      ) : (
        <Form.Item label="High" rules={[RULE_TEXT_FIXED_LEN(10)]}>
          <Input prefix="$" disabled value="MAX" style={{ width: 80 }} />
        </Form.Item>
      )}
      <Form.Item name={'markupPct'} label="Markup">
        <InputNumber<number>
          id={uniqueId('markupPct')}
          min={-100}
          max={100}
          formatter={(v) => `${v}%`}
          parser={(v) => Number(v?.replace('%', ''))}
        ></InputNumber>
      </Form.Item>
      <Form.Item name={'skuReplace'}>
        <Select
          getPopupContainer={(e) => {
            return e as HTMLElement;
          }}
        >
          <Select.Option value={ReplaceTypeEnum.NO_ADDITIONAL} key="1">
            {t('platform.markup.labels.noAdditionalMarkup')}
          </Select.Option>
          {isRevshop && (
            <Select.Option value={ReplaceTypeEnum.NA} key="2">
              {t('platform.markup.labels.appliedMarkup')}
            </Select.Option>
          )}
          {!isRevshop && (
            <Select.Option value={ReplaceTypeEnum.REPLACE_GLOBAL} key="2">
              {t('platform.markup.labels.replaceGlobal')}
            </Select.Option>
          )}
          {!isRevshop && (
            <Select.Option
              value={ReplaceTypeEnum.REPLACE_GLOBAL_AND_ELEMENT}
              key="3"
            >
              {t('platform.markup.labels.replaceGlobalAndElement')}
            </Select.Option>
          )}
        </Select>
      </Form.Item>
      <Form.Item>
        <RevxButton type="link" key="apply" htmlType="submit">
          {t('common.apply')}
        </RevxButton>
      </Form.Item>
    </Form>
  );
};

const SkuMarkupRangeList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const skuMarkup = useAppSelector((state) => state.markup.curSkuMarkup);

  const [ranges, setRanges] = useState<MarkupRange[]>([]);

  useMemo(() => {
    const ranges = cloneDeep(skuMarkup!.markupRanges).sort(
      (a, b) => a.lowCost - b.lowCost
    );
    const highest = ranges.find((r) => !r.hasHighCost);
    if (highest) highest.highCost = Number.POSITIVE_INFINITY;
    setRanges(ranges);
  }, [skuMarkup]);

  const onRangeSave = (toSaveRange: MarkupRange) => {
    if (ranges.length === 0) {
      // this should never happen
      return;
    } else {
      let updatedRanges: MarkupRange[] = [];
      for (let i = 0; i < ranges.length; i++) {
        if (
          ranges[i].highCost <= toSaveRange.lowCost ||
          ranges[i].lowCost >= toSaveRange.highCost
        ) {
          // no overlapping
          updatedRanges.push(clone(ranges[i]));
          continue;
        } else if (ranges[i].lowCost < toSaveRange.lowCost) {
          // right partial overlapping
          const r = clone(ranges[i]);
          r.highCost = toSaveRange.lowCost;
          r.hasHighCost = true;
          updatedRanges.push(r);
          continue;
        } else if (ranges[i].highCost <= toSaveRange.highCost) {
          // full overlapping
          continue;
        } else {
          // left partial overlapping
          ranges[i].lowCost = toSaveRange.highCost;
          updatedRanges.push(clone(ranges[i]));
          continue;
        }
      }
      updatedRanges.push(toSaveRange);
      updatedRanges.sort((a, b) => a.lowCost - b.lowCost);
      setRanges(updatedRanges);
    }
  };

  const onSave = () => {
    if (Boolean(skuMarkup)) {
      const updated: PartialMarkup = clone(skuMarkup!);
      updated.markupRanges = ranges;
      dispatch.markup.saveMarkup(updated);
    }
  };

  return (
    <List
      dataSource={ranges}
      renderItem={(item, index) => {
        return (
          <RangeForm key={index} markupRange={item} onSave={onRangeSave} />
        );
      }}
      footer={
        <Row>
          <Col>
            <RevxButton key="save" onClick={onSave} type="primary">
              {t('common.save')}
            </RevxButton>
          </Col>
        </Row>
      }
    />
  );
};

export default SkuMarkupRangeList;
